import { map, switchMap } from 'rxjs';

import { HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';

import { AuthSessionWithTotpGQL, CreateGuestGQL, GetSelectionJsonGQL, LogoutGQL, MeCleanMemberSessionGQL, MeCleanMemberSessionInput, MeGetLatestMemberAccessPassHistoryListGQL, MeGetLatestMemberAccessPassHistoryListInput, MeGetMemberFacilityBookingHistoryGQL, MeGetMemberFacilityBookingHistoryInput, MeGetMemberSimpleGQL, MeGetOtpGQL, MeGetOwnSessionListGQL, MeInitEshopMemberGQL, MemberLoginGQL, MeUpdateEshopMemberInput, MeUpdateMemberGQL, MeUpdateMemberInput, MeVerifyOtpGQL, MeVerifyOtpInput, } from '../../__generated__/generated';

@Injectable({
  providedIn: 'root',
})
export class MemberApiService {
  private readonly getSelectionJsonGQL = inject(GetSelectionJsonGQL);
  private readonly meGetMemberSimpleGQL = inject(MeGetMemberSimpleGQL);
  private readonly meGetOwnSessionListGQL = inject(MeGetOwnSessionListGQL);
  private readonly meGetLatestMemberAccessPassHistoryListGQL = inject(MeGetLatestMemberAccessPassHistoryListGQL);
  private readonly meGetMemberFacilityBookingHistoryGQL = inject(MeGetMemberFacilityBookingHistoryGQL);
  private readonly createGuestGQL = inject(CreateGuestGQL);
  private readonly meInitEshopMemberGQL = inject(MeInitEshopMemberGQL);
  private readonly memberLoginGQL = inject(MemberLoginGQL);
  private readonly authSessionWithTotpGQL = inject(AuthSessionWithTotpGQL);
  private readonly meUpdateMemberGQL = inject(MeUpdateMemberGQL);
  private readonly logoutGQL = inject(LogoutGQL);
  private readonly meCleanMemberSessionGQL = inject(MeCleanMemberSessionGQL);
  private readonly meGetOtpGQL = inject(MeGetOtpGQL);
  private readonly meVerifyOtpGQL = inject(MeVerifyOtpGQL);

  readonly selectionJson = rxResource({
    stream: () => this.getSelectionJson(),
  });

  // ---------------------------------------------------------------------------
  // Query
  // ---------------------------------------------------------------------------

  getSelectionJson() {
    return this.getSelectionJsonGQL.fetch().pipe(
      map(({ data }) => ({
        sportTypeList: data?.getSelectionJson.sportTypeList ?? [],
        countryList: data?.getSelectionJson.countryList ?? [],
        districtList: data?.getSelectionJson.districtList ?? [],
        locationList: data?.getSelectionJson.locationList ?? [],
        facilityLocationList: data?.getSelectionJson.facilityLocationList ?? [],
      })),
    );
  }

  meGetMemberSimple() {
    return this.meGetMemberSimpleGQL.fetch().pipe(map(({ data }) => data?.meGetMember));
  }

  meGetOwnSessionList() {
    return this.meGetOwnSessionListGQL.fetch().pipe(map(({ data }) => data?.meGetOwnSessionList ?? []));
  }

  meGetLatestMemberAccessPassHistoryList(input: MeGetLatestMemberAccessPassHistoryListInput) {
    return this.meGetLatestMemberAccessPassHistoryListGQL.fetch({ variables: { input } }).pipe(map(({ data }) => data?.meGetLatestMemberAccessPassHistoryList ?? []));
  }

  meGetMemberFacilityBookingHistory(input: MeGetMemberFacilityBookingHistoryInput) {
    return this.meGetMemberFacilityBookingHistoryGQL.fetch({ variables: { input } }).pipe(map(({ data }) => data?.meGetMemberFacilityBookingHistory.itemList ?? []));
  }

  // ---------------------------------------------------------------------------
  // Mutation
  // ---------------------------------------------------------------------------

  createGuest() {
    return this.createGuestGQL.mutate().pipe(map(({ data }) => data?.createGuest));
  }

  meInitEshopMember(input: MeUpdateEshopMemberInput, recaptchaToken: string) {
    return this.meInitEshopMemberGQL.mutate({ variables: { input }, context: { headers: new HttpHeaders({ 'x-recaptcha': recaptchaToken }) } }).pipe(map(({ data }) => data?.meInitEshopMember));
  }

  memberLogin(loginKey: string, password: string, keepLogin: boolean, recaptchaToken: string) {
    return this.memberLoginGQL
      .mutate({
        variables: { input: { loginKey, password, keepLogin } },
        context: {
          headers: new HttpHeaders({ 'x-recaptcha': recaptchaToken }),
        },
      })
      .pipe(map(({ data }) => data?.memberLogin));
  }

  authSessionWithTotp(totp: string, keepLogin: boolean) {
    return this.authSessionWithTotpGQL.mutate({ variables: { input: { totp, keepLogin } } }).pipe(map(({ data }) => data?.authSessionWithTotp.accessToken ?? ''));
  }

  meUpdateMember(input: MeUpdateMemberInput) {
    return this.meUpdateMemberGQL.mutate({ variables: { input } }).pipe(map(({ data }) => data?.meUpdateMember));
  }

  logout() {
    return this.logoutGQL.mutate();
  }

  meCleanMemberSession(input: MeCleanMemberSessionInput) {
    return this.meCleanMemberSessionGQL.mutate({ variables: { input } }).pipe(switchMap(() => this.meGetOwnSessionList()));
  }

  meGetOtp(email: string, recaptchaToken: string) {
    return this.meGetOtpGQL.mutate({
      variables: { input: { email } },
      context: { headers: new HttpHeaders({ 'x-recaptcha': recaptchaToken }) },
    });
  }

  meVerifyOtp(input: MeVerifyOtpInput) {
    return this.meVerifyOtpGQL.mutate({ variables: { input } });
  }
}
